import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, ArrowLeft, Building2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { AuthShell } from "@/pages/Login";

export default function InviteAccept() {
  const { token } = useParams();
  const { loginWithData } = useAuth();
  const nav = useNavigate();
  const [info, setInfo] = useState(null); // null=loading, false=invalid, obj=valid
  const [form, setForm] = useState({ name: "", password: "" });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get(`/auth/invite/${token}`)
      .then((r) => setInfo(r.data))
      .catch(() => setInfo(false));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/accept-invite", { token, name: form.name, password: form.password });
      loginWithData(data);
      toast.success(`Welcome to ${info.organization_name}.`);
      nav("/app");
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  if (info === null) {
    return <AuthShell><div className="flex items-center gap-2 text-muted-foreground"><Loader2 className="w-4 h-4 animate-spin" /> Checking your invite…</div></AuthShell>;
  }

  if (info === false) {
    return (
      <AuthShell>
        <h1 className="font-serif text-3xl font-semibold">Invite not valid</h1>
        <p className="text-muted-foreground text-sm mt-2">This invite link is invalid or has expired. Ask your workspace owner to send a new one.</p>
        <Link to="/login" className="inline-flex items-center gap-1.5 text-sm text-foreground underline underline-offset-4 mt-6" data-testid="invite-to-login">
          <ArrowLeft className="w-4 h-4" /> Go to sign in
        </Link>
      </AuthShell>
    );
  }

  return (
    <AuthShell>
      <div className="flex items-center gap-2 mb-6" data-testid="invite-org">
        <Building2 className="w-4 h-4 text-brand" />
        <span className="text-sm text-muted-foreground">You're invited to</span>
        <span className="text-sm font-medium">{info.organization_name}</span>
        <Badge variant="outline" className="rounded-none capitalize text-[10px]">{info.role}</Badge>
      </div>
      <h1 className="font-serif text-3xl font-semibold">Join the workspace</h1>
      <p className="text-muted-foreground text-sm mt-2">Set up your account for <span className="text-foreground font-medium">{info.email}</span>.</p>

      <form onSubmit={submit} className="mt-8 space-y-4">
        <div>
          <Label htmlFor="email">Work email</Label>
          <Input id="email" value={info.email} disabled className="mt-1.5 rounded-none opacity-70" data-testid="invite-email" />
        </div>
        <div>
          <Label htmlFor="name">Full name</Label>
          <Input id="name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5 rounded-none" placeholder="Jane Counsel" data-testid="invite-name" />
        </div>
        <div>
          <Label htmlFor="password">Password</Label>
          <Input id="password" type="password" required minLength={8} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="mt-1.5 rounded-none" placeholder="At least 8 characters" data-testid="invite-password" />
        </div>
        <Button type="submit" className="w-full rounded-none" disabled={loading} data-testid="invite-submit">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Join workspace"}
        </Button>
      </form>
    </AuthShell>
  );
}
